import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { Spinner } from "@/components/kit";
import { FEATURES } from "@/data/paywall";
import { toolSource, track } from "@/lib/toolTrack";

export default function Paywall() {
  const { user, loading, logout } = useAuth();
  const [providers, setProviders] = useState({ paypal: true });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/payment/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  if (loading) return <Spinner className="min-h-screen items-center" />;
  if (!user) return <Navigate to="/checkout" replace />;
  if (user.has_paid) return <Navigate to="/" replace />;

  const pay = async () => {
    setBusy(true);
    try {
      const src = toolSource();
      if (src) track(src, "checkout");
      const { data } = await api.post("/payment/create-checkout", { origin_url: window.location.origin, device_id: user.id, user_id: user.id });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen mm-gradient-soft flex items-center justify-center px-5 py-12">
      <div className="max-w-lg w-full mm-card p-8 sm:p-10 mm-fade-up" data-testid="paywall-card">
        <div className="flex justify-between items-start">
          <div className="w-14 h-14 rounded-2xl mm-gradient flex items-center justify-center text-2xl">🧠</div>
          <button onClick={async () => { await logout(); nav("/landing"); }} className="text-mm-muted text-sm flex items-center gap-1" data-testid="paywall-logout"><LogOut size={14} /> Log out</button>
        </div>
        {params.get("cancelled") && (
          <div className="mm-soft px-4 py-3 mt-5 text-sm" role="status" data-testid="paywall-cancelled-notice">Payment cancelled. You can try again anytime.</div>
        )}
        <h1 className="text-3xl font-bold mt-5">Unlock Mindful Me</h1>
        <p className="text-mm-muted mt-1">Hi {user.name.split(" ")[0]} — one payment, lifetime access.</p>
        <ul className="grid sm:grid-cols-2 gap-3 mt-6">
          {FEATURES.map((f) => (
            <li key={f.text} className="flex items-center gap-2 text-sm"><span style={{ color: f.color }}><Icon name={f.icon} size={18} /></span>{f.text}</li>
          ))}
        </ul>
        <div className="mm-soft p-5 mt-8 flex items-baseline gap-2">
          <span className="font-display text-4xl font-extrabold" data-testid="paywall-price">$19.99</span>
          <span className="text-mm-muted text-sm">one-time · lifetime</span>
        </div>
        <button className="mm-btn w-full mt-6" onClick={pay} disabled={busy || !providers.paypal} data-testid="paywall-paypal-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Pay with PayPal
        </button>
        <p className="text-xs text-mm-muted text-center mt-4">Secure checkout with PayPal. Pay with your PayPal balance or a card through PayPal.</p>
      </div>
    </div>
  );
}
